import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Popover, Typography, Stack, Button, IconButton, Divider, Skeleton, alpha,
} from '@mui/material';
import { DoneAll, Circle } from '@mui/icons-material';
import api from '../../services/api';

interface AppNotification {
  _id: string;
  title?: string;
  body?: string;
  status?: string;
  createdAt?: string;
}

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export default function NotificationPanel({ anchorEl, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const open = Boolean(anchorEl);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'panel'],
    queryFn: async () => {
      const res = await api.get('/notifications', { params: { page: 1, limit: 20 } });
      return {
        rows: (res.data.data || []) as AppNotification[],
        unread: Number(res.data.meta?.unreadCount ?? 0),
      };
    },
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  const rows = data?.rows || [];
  const unread = data?.unread ?? 0;

  const openFullPage = () => {
    onClose();
    navigate('/notifications');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            width: { xs: 320, sm: 380 },
            maxHeight: 480,
            borderRadius: 2.5,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>Notifications</Typography>
          <Typography variant="caption" color="text.secondary">
            {unread > 0 ? `${unread} unread` : 'You are up to date'}
          </Typography>
        </Box>
        {unread > 0 && (
          <IconButton
            size="small"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            aria-label="Mark all as read"
          >
            <DoneAll fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Divider />
      <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
        {isLoading && (
          <Stack spacing={1} p={2}>
            <Skeleton height={52} />
            <Skeleton height={52} />
            <Skeleton height={52} />
          </Stack>
        )}
        {!isLoading && rows.length === 0 && (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
          </Box>
        )}
        {rows.map((n) => {
          const unreadItem = n.status !== 'read';
          return (
            <Box
              key={n._id}
              onClick={() => { if (unreadItem) markRead.mutate(n._id); }}
              sx={{
                px: 2,
                py: 1.25,
                cursor: unreadItem ? 'pointer' : 'default',
                bgcolor: unreadItem ? alpha('#2563eb', 0.06) : 'transparent',
                borderBottom: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: alpha('#2563eb', 0.08) },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {unreadItem && <Circle sx={{ fontSize: 8, color: 'primary.main', mt: 0.9 }} />}
                <Box minWidth={0} flex={1}>
                  <Typography variant="body2" fontWeight={unreadItem ? 800 : 600} noWrap>
                    {n.title || 'Notification'}
                  </Typography>
                  {n.body && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                      {n.body}
                    </Typography>
                  )}
                  {n.createdAt && (
                    <Typography variant="caption" color="text.disabled">
                      {new Date(n.createdAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Box>
      <Divider />
      <Box p={1.25} textAlign="center">
        <Button size="small" onClick={openFullPage} sx={{ fontWeight: 700, textTransform: 'none' }}>
          View all notifications
        </Button>
      </Box>
    </Popover>
  );
}
